import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Search, Filter, ChevronRight, CheckCircle2, XCircle, 
  Clock, Phone, Trash2, MoreHorizontal, UserPlus, X, Download
} from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { getLeads, deleteLead, updateLead, createManualLead } from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status, isQualified }) => {
  let badgeClass = 'status-badge ';
  
  if (status === 'converted') badgeClass += 'converted';
  else if (status === 'contacted') badgeClass += 'contacted';
  else if (isQualified) badgeClass += 'qualified';
  else if (status === 'unqualified') badgeClass += 'unqualified';
  else badgeClass += 'new';

  return (
    <span className={badgeClass} data-testid={`status-badge-${status}`}>
      {status === 'converted' ? 'Converted' :
       status === 'contacted' ? 'Contacted' :
       isQualified ? 'Qualified' :
       status === 'unqualified' ? 'Unqualified' : 'New'}
    </span>
  );
};

export default function AdminLeads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingLead, setAddingLead] = useState(false);
  const [newLead, setNewLead] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    source: 'manual', notes: '', available_capital: '', status: 'new'
  });

  const fetchLeads = async () => {
    try {
      const filters = {};
      if (statusFilter !== 'all') {
        if (statusFilter === 'qualified') {
          filters.is_qualified = true;
        } else if (statusFilter === 'unqualified') {
          filters.is_qualified = false;
        } else {
          filters.status = statusFilter;
        }
      }
      const data = await getLeads(filters);
      setLeads(data);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [statusFilter]);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    
    try {
      await deleteLead(id);
      toast.success('Lead deleted');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const handleStatusChange = async (id, newStatus, e) => {
    e.stopPropagation();
    try {
      await updateLead(id, { status: newStatus });
      toast.success('Status updated');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAddLead = async (e) => {
    e.preventDefault();
    if (!newLead.first_name || !newLead.email || !newLead.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    setAddingLead(true);
    try {
      await createManualLead(newLead);
      toast.success('Lead added successfully');
      setShowAddModal(false);
      setNewLead({ first_name: '', last_name: '', email: '', phone: '', source: 'manual', notes: '', available_capital: '', status: 'new' });
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add lead');
    } finally {
      setAddingLead(false);
    }
  };

  const handleExportCSV = () => {
    const token = localStorage.getItem('lbu_admin_token');
    const url = `${BACKEND_URL}/api/admin/leads/export`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (!res.ok) throw new Error('Export failed');
        return res.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `leads_export.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success('CSV exported');
      })
      .catch(() => toast.error('Failed to export CSV'));
  };

  const filteredLeads = leads.filter(lead => {
    const searchLower = searchTerm.toLowerCase();
    return (
      lead.first_name?.toLowerCase().includes(searchLower) ||
      lead.last_name?.toLowerCase().includes(searchLower) ||
      lead.email?.toLowerCase().includes(searchLower) ||
      lead.phone?.includes(searchTerm)
    );
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <AdminLayout title="Leads">
      {/* Filters Bar */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, email, or phone..."
            className="input-dark pl-12"
            data-testid="search-input"
          />
        </div>
        
        <Button onClick={handleExportCSV} variant="outline" className="border-white/10 text-white/60 hover:text-white" data-testid="export-csv-btn">
          <Download className="w-5 h-5 mr-2" /> Export CSV
        </Button>
        <Button onClick={() => setShowAddModal(true)} className="btn-primary" data-testid="add-lead-btn">
          <UserPlus className="w-5 h-5 mr-2" /> Add Lead
        </Button>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {['all', 'new', 'qualified', 'unqualified', 'contacted', 'converted'].map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              statusFilter === status 
                ? 'bg-rtb-purple text-black' 
                : 'bg-charcoal text-white/60 hover:text-white'
            }`}
            data-testid={`filter-${status}`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Add Lead Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="add-lead-modal">
          <div className="bg-charcoal border border-white/10 w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-oswald text-xl font-bold">Add New Lead</h3>
              <button onClick={() => setShowAddModal(false)} className="text-white/40 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleAddLead} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/60 mb-1.5 block text-sm">First Name *</Label>
                  <Input
                    value={newLead.first_name}
                    onChange={(e) => setNewLead({ ...newLead, first_name: e.target.value })}
                    className="input-dark" required
                    data-testid="manual-first-name"
                  />
                </div>
                <div>
                  <Label className="text-white/60 mb-1.5 block text-sm">Last Name</Label>
                  <Input
                    value={newLead.last_name}
                    onChange={(e) => setNewLead({ ...newLead, last_name: e.target.value })}
                    className="input-dark"
                    data-testid="manual-last-name"
                  />
                </div>
              </div>

              <div>
                <Label className="text-white/60 mb-1.5 block text-sm">Email *</Label>
                <Input
                  type="email"
                  value={newLead.email}
                  onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                  className="input-dark" required
                  data-testid="manual-email"
                />
              </div>

              <div>
                <Label className="text-white/60 mb-1.5 block text-sm">Phone *</Label>
                <Input
                  type="tel"
                  value={newLead.phone}
                  onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                  className="input-dark" required
                  data-testid="manual-phone"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/60 mb-1.5 block text-sm">Source</Label>
                  <select
                    value={newLead.source}
                    onChange={(e) => setNewLead({ ...newLead, source: e.target.value })}
                    className="w-full h-11 bg-white/5 border border-white/10 text-white px-3 focus:border-rtb-purple focus:outline-none text-sm"
                    data-testid="manual-source"
                  >
                    <option value="manual" className="bg-charcoal">Manual</option>
                    <option value="referral" className="bg-charcoal">Referral</option>
                    <option value="phone" className="bg-charcoal">Phone Call</option>
                    <option value="event" className="bg-charcoal">Event</option>
                    <option value="facebook" className="bg-charcoal">Facebook</option>
                    <option value="instagram" className="bg-charcoal">Instagram</option>
                  </select>
                </div>
                <div>
                  <Label className="text-white/60 mb-1.5 block text-sm">Available Capital</Label>
                  <select
                    value={newLead.available_capital}
                    onChange={(e) => setNewLead({ ...newLead, available_capital: e.target.value })}
                    className="w-full h-11 bg-white/5 border border-white/10 text-white px-3 focus:border-rtb-purple focus:outline-none text-sm"
                    data-testid="manual-capital"
                  >
                    <option value="" className="bg-charcoal">Unknown</option>
                    <option value="under_1000" className="bg-charcoal">Under $1,000</option>
                    <option value="1000_2500" className="bg-charcoal">$1,000 - $2,500</option>
                    <option value="2500_5000" className="bg-charcoal">$2,500 - $5,000</option>
                    <option value="5000_10000" className="bg-charcoal">$5,000 - $10,000</option>
                    <option value="over_10000" className="bg-charcoal">Over $10,000</option>
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-white/60 mb-1.5 block text-sm">Notes</Label>
                <textarea
                  value={newLead.notes}
                  onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                  className="w-full min-h-[80px] bg-white/5 border border-white/10 text-white p-3 focus:border-rtb-purple focus:outline-none text-sm"
                  placeholder="Any notes about this lead..."
                  data-testid="manual-notes"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="flex-1 border-white/10 text-white/60 hover:text-white">
                  Cancel
                </Button>
                <Button type="submit" disabled={addingLead} className="btn-primary flex-1" data-testid="manual-submit-btn">
                  {addingLead ? 'Adding...' : 'Add Lead'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Leads Table */}
      <div className="bg-charcoal border border-white/10 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-rtb-purple border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-white/40">
            <p>No leads found</p>
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="mt-2 text-rtb-purple hover:underline"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="leads-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Capital</th>
                  <th>Score</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead) => (
                  <tr 
                    key={lead.id}
                    onClick={() => navigate(`/admin/leads/${lead.id}`)}
                    className="cursor-pointer"
                    data-testid={`lead-row-${lead.id}`}
                  >
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-rtb-purple/10 rounded-full flex items-center justify-center">
                          <span className="text-rtb-purple font-bold">
                            {lead.first_name?.[0]}{lead.last_name?.[0]}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{lead.first_name} {lead.last_name}</p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <p className="text-sm">{lead.email}</p>
                      <p className="text-sm text-white/40">{lead.phone}</p>
                    </td>
                    <td>
                      <span className={`text-sm ${
                        ['2500_5000', '5000_10000', 'over_10000'].includes(lead.qualification?.available_capital)
                          ? 'text-green-400'
                          : 'text-white/60'
                      }`}>
                        {lead.qualification?.available_capital?.replace(/_/g, '-').replace('over', '10k+') || '-'}
                      </span>
                    </td>
                    <td>
                      <span className="font-oswald text-lg text-rtb-purple">
                        {lead.qualification_score || 0}
                      </span>
                    </td>
                    <td>
                      <StatusBadge status={lead.status} isQualified={lead.is_qualified} />
                    </td>
                    <td className="text-white/60 text-sm">
                      {formatDate(lead.created_at)}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-white/40 hover:text-white">
                            <MoreHorizontal className="w-5 h-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-charcoal border-white/10">
                          <DropdownMenuItem 
                            onClick={(e) => handleStatusChange(lead.id, 'contacted', e)}
                            className="cursor-pointer"
                          >
                            <Phone className="w-4 h-4 mr-2" /> Mark Contacted
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={(e) => handleStatusChange(lead.id, 'converted', e)}
                            className="cursor-pointer"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" /> Mark Converted
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={(e) => handleDelete(lead.id, e)}
                            className="cursor-pointer text-signal-red focus:text-signal-red"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="mt-4 text-white/40 text-sm">
        Showing {filteredLeads.length} of {leads.length} leads
      </div>
    </AdminLayout>
  );
}
